'use client';
// Mini 3D Q×V×Z scatter showing only the active portfolio holdings at a
// selectable historical month. Mirrors AlphaZoneVisualizer but scoped to
// portfolio.json symbols instead of the alpha basket. Mounted at the top
// of the /portfolio Overview tab.
//
// Time slider:
//   - Far right: TODAY (live /api/stocks → full ScoredStock with factors,
//     so click on a planet opens the full StockDetail popup)
//   - Going back: monthly snapshots from the full-monthly cache, filtered
//     to portfolio symbols. Shows the holdings as they would have looked
//     each month — useful for "where was AMZN's quality 3 years ago?"

import { useMemo, useState } from 'react';
import { useResource } from '@/lib/use-resource';
import { ScatterPlot3D } from '@/components/ScatterPlot3D';
import { StockDetail } from '@/components/StockDetail';
import { PortfolioEventsFeed } from '@/components/PortfolioEventsFeed';
import type { ScoredStock, StockFactors } from '@/lib/compute';
import type { TechnicalStock } from '@/lib/hooks/use-visualizer-data';
import type { PillarScores } from '@/lib/types';
import { computeSuperPlanet } from '@/lib/portfolio-super-planet';
import { prodQuality } from '@/lib/composite-score';

interface StocksResponse { stocks: ScoredStock[]; updatedAt?: string; }
interface PortfolioResponse {
  symbols: string[];
  weights?: Record<string, number>;
  cashWeight?: number;
}

/** Synthetic ScoredStock representing the whole book at weighted-avg coords.
 *  Cube-root sphere sizing: radius = (mcap/1e9)^(1/3) * 0.035. At 100T this
 *  yields ~1.62 world units — about 3× the biggest individual stock (~$3T
 *  AAPL → radius ~0.5). Reads as a burning sun against the dim universe. */
const SUPER_PLANET_MARKET_CAP = 51_000_000_000_000; // 51T → ~20% smaller than first cut
const SUPER_PLANET_SYMBOL = '☼';                     // sun glyph
const SUPER_PLANET_NAME = 'Portfolio (Combined)';
interface MonthlyMonthsResponse { months: string[]; topPerMonth?: number; }
interface MonthlyStock {
  symbol: string;
  name?: string;
  sector: string;
  industry?: string;
  qualityScore: number;
  valuationScore: number;
  composite: number;
  price: number | null;
  marketCap?: number;
  priceMomentumZ?: number;
}
interface MonthlyMonthResponse { month: string; topPerMonth?: number; stocks: MonthlyStock[]; }

const EMPTY_FACTORS = {} as StockFactors;
const EMPTY_PILLARS: PillarScores = {
  profitability: null, growth: null, safety: null, shareholderReturns: null, momentum: null,
};

function monthlyToScoredStock(m: MonthlyStock): ScoredStock {
  return {
    symbol: m.symbol,
    name: m.name ?? m.symbol,
    sector: m.sector,
    industry: m.industry ?? m.sector,
    marketCap: m.marketCap ?? 0,
    price: m.price ?? 0,
    factors: EMPTY_FACTORS,
    pillarScores: EMPTY_PILLARS,
    qualityScore: m.qualityScore,
    valuationScore: m.valuationScore,
    momentumScore: m.priceMomentumZ ?? 0,
  };
}

function fmtMonth(m: string): string {
  const [y, mm] = m.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(mm,10)-1]} ${y}`;
}

export function PortfolioVisualizer() {
  const stocksResp = useResource<StocksResponse>('/api/stocks');
  const portfolioResp = useResource<PortfolioResponse>('/api/portfolio');
  const monthsResp = useResource<MonthlyMonthsResponse>('/api/full-monthly');

  const months = useMemo(() => monthsResp.data?.months ?? [], [monthsResp.data]);
  const stops = useMemo(() => [...months, 'today'], [months]);

  const [stopIdx, setStopIdx] = useState(() => Math.max(0, stops.length - 1));
  const [stopsLength, setStopsLength] = useState(stops.length);
  if (stops.length !== stopsLength) {
    setStopsLength(stops.length);
    if (stops.length > 0) setStopIdx(stops.length - 1);
  }

  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
  const [prevStopIdx, setPrevStopIdx] = useState(stopIdx);
  if (stopIdx !== prevStopIdx) {
    setPrevStopIdx(stopIdx);
    if (selectedSymbol) setSelectedSymbol(null);
  }

  const isToday = stops[stopIdx] === 'today';
  const monthKey = isToday ? null : stops[stopIdx];

  const monthlyResp = useResource<MonthlyMonthResponse>(
    monthKey ? `/api/full-monthly?month=${monthKey}` : null,
  );

  const allStocksToday: ScoredStock[] = useMemo(
    () => stocksResp.data?.stocks ?? [],
    [stocksResp.data],
  );

  const portfolioSymbolSet = useMemo(
    () => new Set(portfolioResp.data?.symbols ?? []),
    [portfolioResp.data],
  );

  // V mode: 'blended' (production default) reads valuationScoreBlended from
  // scores.json. 'classic' reverts to the peer-z valuationScore. Toggle on
  // the 3D scatter overlay (next to Top 15 Q). Steve directive 2026-05-08.
  const [vMode, setVMode] = useState<'classic' | 'blended'>('blended');

  // Combined mode: collapses the book into a single super-planet at the
  // weighted-average Q/V/momentum coordinates. Replaces the universe-only
  // "Top 15 Q" tab (which is meaningless on a 17-stock portfolio view) —
  // Steve directive 2026-05-22.
  const [tierFilter, setTierFilter] = useState<string>('all');
  const isCombined = tierFilter === 'combined';

  // Wrap in useMemo so the {} default isn't a fresh object every render —
  // otherwise useMemo below treats it as a changed dep and recomputes
  // (and re-renders) on every parent render even when weights are stable.
  const portfolioWeights = useMemo(
    () => portfolioResp.data?.weights ?? {},
    [portfolioResp.data?.weights],
  );

  const { highlightSet, plotStocks } = useMemo(() => {
    const applyV = (s: ScoredStock): ScoredStock => {
      if (vMode === 'classic') return s;
      const blended = s.valuationScoreBlended;
      return blended != null && Number.isFinite(blended) ? { ...s, valuationScore: blended } : s;
    };

    // Build the per-holding stock list first; "combined" then collapses
    // it into a single synthetic super-planet at the weighted-avg coords.
    let holdings: ScoredStock[] = [];
    let highlight: Set<string> = new Set();
    if (isToday) {
      holdings = allStocksToday.filter(s => portfolioSymbolSet.has(s.symbol)).map(applyV);
      highlight = portfolioSymbolSet;
    } else if (monthlyResp.data) {
      const matched = monthlyResp.data.stocks
        .filter(s => portfolioSymbolSet.has(s.symbol))
        .filter(s => Number.isFinite(s.qualityScore) && Number.isFinite(s.valuationScore));
      holdings = matched.map(monthlyToScoredStock);
      highlight = new Set(matched.map(s => s.symbol));
    }

    if (!isCombined || holdings.length === 0) {
      return { highlightSet: highlight, plotStocks: holdings };
    }

    // Combined mode: weighted-average the holdings into one super-planet.
    // Weights are the live portfolio weights from /api/portfolio (shares ×
    // today's price ÷ NAV). Cash is excluded from the average — it has no
    // Q/V to contribute. Historical view falls back to equal weights since
    // we don't snapshot per-month weights.
    const weightFor = (sym: string): number => {
      if (isToday && portfolioWeights[sym] != null) return portfolioWeights[sym];
      return 1 / holdings.length; // equal-weight fallback (historical view)
    };
    const aggregate = computeSuperPlanet(
      holdings.map(s => ({
        symbol: s.symbol,
        qualityScore: prodQuality(s),
        valuationScore: s.valuationScore,
        momentumScore: s.momentumScore,
        marketCap: s.marketCap,
        weight: weightFor(s.symbol),
      })),
    );
    if (!aggregate) {
      return { highlightSet: highlight, plotStocks: holdings };
    }

    const superPlanet: ScoredStock = {
      symbol: SUPER_PLANET_SYMBOL,
      name: SUPER_PLANET_NAME,
      sector: 'Portfolio',
      industry: 'Aggregate',
      marketCap: SUPER_PLANET_MARKET_CAP,
      price: 0,
      factors: EMPTY_FACTORS,
      pillarScores: EMPTY_PILLARS,
      qualityScore: aggregate.qualityScore,
      valuationScore: aggregate.valuationScore,
      momentumScore: aggregate.momentumScore,
    };
    return {
      highlightSet: new Set<string>([SUPER_PLANET_SYMBOL]),
      plotStocks: [superPlanet],
    };
  }, [isToday, portfolioSymbolSet, allStocksToday, monthlyResp.data, vMode, isCombined, portfolioWeights]);

  const selectedStock = useMemo(() => {
    if (!selectedSymbol) return null;
    return allStocksToday.find(s => s.symbol === selectedSymbol) ?? null;
  }, [selectedSymbol, allStocksToday]);

  if (stocksResp.loading || portfolioResp.loading || monthsResp.loading) {
    return (
      <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg p-8 text-center text-[#64748b] text-sm">
        loading portfolio visualizer…
      </div>
    );
  }

  const stopLabel = isToday ? 'TODAY' : fmtMonth(monthKey!);
  const totalStops = stops.length;
  const portfolioSize = portfolioSymbolSet.size;

  return (
    <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg overflow-hidden">
      <div className="flex items-baseline justify-between px-4 py-3 border-b border-[#1e293b]">
        <div className="flex items-baseline gap-3">
          <span className="text-[11px] uppercase tracking-wide text-[#94a3b8] font-semibold">
            Portfolio Visualizer
          </span>
          <span className="text-[10px] text-[#64748b]">
            {plotStocks.length} of {portfolioSize} holdings
            {!isToday ? ` · click to see today's detail` : ''}
          </span>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-[#94a3b8]">
          <span className="uppercase tracking-wider text-[#64748b]">View as of</span>
          <button
            onClick={() => setStopIdx(Math.max(0, stopIdx - 12))}
            className="text-[#64748b] hover:text-amber-300 px-1"
            title="Back 1 year"
          >‹‹</button>
          <button
            onClick={() => setStopIdx(Math.max(0, stopIdx - 1))}
            className="text-[#64748b] hover:text-amber-300 px-1"
            title="Back 1 month"
          >‹</button>
          <input
            type="range"
            min={0}
            max={totalStops - 1}
            value={stopIdx}
            onChange={e => setStopIdx(parseInt(e.target.value, 10))}
            className="w-[280px] accent-amber-400"
          />
          <button
            onClick={() => setStopIdx(Math.min(totalStops - 1, stopIdx + 1))}
            className="text-[#64748b] hover:text-amber-300 px-1"
            title="Forward 1 month"
          >›</button>
          <button
            onClick={() => setStopIdx(Math.min(totalStops - 1, stopIdx + 12))}
            className="text-[#64748b] hover:text-amber-300 px-1"
            title="Forward 1 year"
          >››</button>
          <button
            onClick={() => setStopIdx(totalStops - 1)}
            className="text-[10px] text-amber-300/70 hover:text-amber-300 uppercase tracking-wide font-mono"
          >Today</button>
          <span className="font-mono font-semibold text-amber-300 min-w-[5rem] text-right">{stopLabel}</span>
        </div>
      </div>

      <div className="px-4 pt-1 pb-2 flex items-center justify-end gap-0.5 text-[8px] text-[#475569] font-mono overflow-x-auto">
        {stops.map((s, i) => {
          const isJan = s.endsWith('-01');
          const isCurrent = i === stopIdx;
          if (!isJan && !isCurrent && s !== 'today') return null;
          return (
            <button
              key={s + '-' + i}
              onClick={() => setStopIdx(i)}
              className={`px-1 cursor-pointer transition-colors hover:text-[#94a3b8] ${isCurrent ? 'text-amber-300' : ''}`}
            >
              {s === 'today' ? 'NOW' : s.slice(0, 4)}
            </button>
          );
        })}
      </div>

      <div className="relative" style={{ height: 480 }}>
        {plotStocks.length === 0 ? (
          <div className="flex items-center justify-center h-full text-[#64748b] text-sm">
            {monthlyResp.loading ? 'loading snapshot…' : 'no portfolio holdings in this snapshot'}
          </div>
        ) : (
          <>
            <div className="flex h-full">
              <ScatterPlot3D
                stocks={plotStocks}
                totalCount={plotStocks.length}
                technicalData={[] as TechnicalStock[]}
                technicalWeeklyData={[] as TechnicalStock[]}
                technicalMonthlyData={[] as TechnicalStock[]}
                highlightSymbol={''}
                onSelectStock={(sym) => {
                  // Clicking the super-planet is a no-op (no underlying stock
                  // to drill into). Per-holding clicks behave normally.
                  if (sym === SUPER_PLANET_SYMBOL) return;
                  setSelectedSymbol(sym);
                }}
                selectedSymbol={selectedSymbol}
                alphaZoneStocks={highlightSet}
                // Mark the super-planet as factor-highlighted so StockPoint
                // applies the bright emissive (3×) + slow pulse — sun feel.
                factorHighlightStocks={isCombined ? new Set([SUPER_PLANET_SYMBOL]) : undefined}
                hideEarningsCalendar
                legendPosition="bottom-right"
                vMode={vMode}
                onVModeChange={setVMode}
                tierButtons={[{
                  key: 'combined',
                  label: 'Combined',
                  title: 'Collapse all holdings into one super-planet at the weighted-avg Q/V/momentum',
                }]}
                onTierFilterChange={(next) => setTierFilter(next)}
              />
            </div>
            {/* Bottom-left alerts overlay — mirrors the AlphaZoneVisualizer
                pattern. Compact PortfolioEventsFeed scoped to today's portfolio
                symbols. Same w-[200px] / max-height 300px / left-3 bottom-3
                placement so the two visualizers read as siblings. */}
            {isToday && Array.from(portfolioSymbolSet).length > 0 && (
              <div
                className="absolute left-3 bottom-3 z-20 w-[200px] bg-[#0d1117]/95 border border-[#1e293b] rounded-lg shadow-xl backdrop-blur-sm overflow-hidden"
                title="Recent BBG alerts scoped to current portfolio"
              >
                <PortfolioEventsFeed
                  title="Portfolio Alerts"
                  symbols={Array.from(portfolioSymbolSet)}
                  onSelectSymbol={(sym) => setSelectedSymbol(sym)}
                  compact
                  maxHeightPx={300}
                />
              </div>
            )}
          </>
        )}
      </div>

      {selectedStock && (
        <StockDetail
          stock={selectedStock}
          allStocks={allStocksToday}
          pinnedSymbols={new Set()}
          onTogglePin={() => {}}
          onClose={() => setSelectedSymbol(null)}
          isAlphaZone
        />
      )}
    </div>
  );
}
